import { useEffect, useState, useContext } from 'react'
import { Container, Typography, Box } from '@mui/material'
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart'
import { useTheme } from '@mui/material/styles'
import { HighlightItemData } from '@mui/x-charts/context'
import Title from '../title/Title'
import { PieItemIdentifier } from '@mui/x-charts'
import React from 'react'
import { IntakeContext } from 'context/IntakeContext'

import PieLabels from '../labels/PieLabels'

import { useRef, useLayoutEffect } from 'react'

// interface DataItem {
//   value: number
//   color: string
//   title: string
// }

// interface PieProps {
//   data: DataItem[]
//   onSelectionChange: (selected: HighlightItemData | null) => void
//   selected: HighlightItemData | null
// }

// function Pie({ data, onSelectionChange, selected }: PieProps) {

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

function Pie({
  data,
  onSelectionChange,
  selected,
  viewSelection,
  hideLabels = true,
  redact = false,
}) {
  const { intakeState } = useContext(IntakeContext)

  const theme = useTheme()

  const [selectedItem, setSelectedItem] = useState('TSP Total')
  const [hoveredItem, setHoveredItem] = useState(null)

  const tspBalance =
    (intakeState.tspBalance ?? 0) + (intakeState.tspRothBalance ?? 0)

  const tspRoth = intakeState?.tspRothBalance ?? 0
  const tspTraditional = tspBalance - tspRoth

  useEffect(() => {
    if (selected) {
      setSelectedItem(selected)
    } else {
      resetSelection()
    }
  }, [selected])

  const resetSelection = () => {
    setSelectedItem('TSP Total')
    setHoveredItem(null)
  }

  const handleHighlightChange = (highlight) => {
    setHoveredItem(highlight)
  }

  const handleItemClick = (_event, pieItemIdentifier) => {
    if (selectedItem?.dataIndex === pieItemIdentifier?.dataIndex) {
      resetSelection()
      onSelectionChange(null)
    } else {
      setSelectedItem(pieItemIdentifier)
      setHoveredItem(pieItemIdentifier)
      onSelectionChange(pieItemIdentifier)
    }
  }

  const getHighlightedItem = () => {
    return hoveredItem ?? selected
  }

  const getValue = () => {
    const activeItem = hoveredItem || selectedItem

    if (
      activeItem &&
      typeof activeItem.dataIndex === 'number' &&
      data[activeItem.dataIndex]
    ) {
      return data[activeItem.dataIndex].value
    }

    if (viewSelection === 'TSP Roth') return tspRoth
    if (viewSelection === 'TSP Traditional') return tspTraditional

    return tspBalance
  }

  // FOR PIE CHART LABELS
  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      setDimensions({ width, height })
    })

    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const cx = dimensions.width / 2
  const cy = dimensions.height / 2
  const radius = Math.min(cx, cy)

  const fundCount = data?.length ?? 0
  const isCrowded = fundCount > 4

  return (
    <Container
      ref={containerRef}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        height: redact
          ? { xs: 150, md: 180 }
          : isCrowded
          ? { xs: 200, md: 260 } // smaller when lots of funds
          : { xs: 250, md: 350 },
        overflow: 'visible',
      }}
    >
      <PieChart
        series={[
          {
            id: 'A',
            highlightScope: { highlight: 'item' },
            highlighted: { additionalRadius: 10 },
            data,
            innerRadius: '89%',
            outerRadius: '100%',
            paddingAngle: 2.5,
            cornerRadius: 0,
            startAngle: 0,
            endAngle: 360,
            // cx: '62%',
            // cy: '50%',
          },
        ]}
        slotProps={{ legend: { hidden: true } }}
        sx={{
          ml: 8,
          scale: 0.95,
          overflow: 'visible',
          '.MuiChart-highlighted': {
            fill: 'transparent',
            strokeWidth: 2,
          },
        }}
        onHighlightChange={handleHighlightChange}
        highlightedItem={getHighlightedItem()}
        onItemClick={handleItemClick}
        tooltip={{ trigger: 'none' }}
      />

      {!hideLabels && (
        <>
          {dimensions.width > 0 && (
            <PieLabels
              data={data.map((d) => ({
                value: d.value,
                label: d.title,
                color: d.color,
              }))}
              cx={cx}
              cy={cy}
              radius={radius}
            />
          )}
        </>
      )}

      <Container
        sx={{
          position: 'absolute',
          left: -10,

          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Title
          highlighted={
            typeof getHighlightedItem()?.dataIndex === 'number'
              ? getHighlightedItem()?.dataIndex
              : typeof selected?.dataIndex === 'number'
              ? selected?.dataIndex
              : null
          }
          data={data}
          fallbackTitle={viewSelection}
        />
        <Typography
          variant="headerTitle"
          align="center center"
          sx={{
            mt: { xs: 0, md: 1 },
            fontSize: redact ? { xs: 16, md: 24 } : 'inherit',
            textAlign: 'center',
            alignSelf: 'center',
            justifyContent: 'center',
            display: 'flex',
            color: '#373B47',
            justifySelf: 'center',
          }}
        >
          {redact ? '$XXX,XXX' : dollarFormat.format(getValue())}
        </Typography>
      </Container>
    </Container>
  )
}

export default Pie
